import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import {
  UserOrganizationsList,
  Button,
  SuccessNotification,
  CreateOrganizationModal,
  JoinOrganizationModal,
  NotificationModal,
} from '@sistemiv/s-components'
import { useMsal } from '@azure/msal-react'
import { useCreateOrganization } from '../../repositories/organizations/mutations/create-organization.mutation'
import { useMyOrganizations } from '../../repositories/organizations/organizations-my.respository'
import { useOrganizations } from '../../repositories/organizations/organizations.respository'
import { useJoinOrganization } from '../../repositories/organizations/mutations/join-organization.mutation'

const Organizations: FC = () => {
  const { instance } = useMsal()
  const currentId = useMemo(() => {
    const accounts = instance.getAllAccounts()
    if (accounts[0]) {
      return accounts[0].localAccountId
    }
    return undefined
  }, [instance])
  const [createOrganizationOpen, setCreateOrganizationOpen] = useState(false)
  const [joinOrganizationOpen, setJoinOrganizationOpen] = useState(false)
  const [notificationModalOpen, setNotificationModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [notificationMessage, setNotificationMessage] = useState('')
  const [search, setSearch] = useState('')
  const {
    data: myOrgPages,
    fetchNextPage: fetchNextMyOrgs,
    hasNextPage: hasNextMyOrgs,
    isFetching: isFetchingMyOrg,
  } = useMyOrganizations({ userId: currentId })
  const { data: orgPages, fetchNextPage: fetchNextOrgs } = useOrganizations({ userId: currentId, search: search })
  const { mutate: createOrganization, isPending: isCreating } = useCreateOrganization()
  const { mutate: joinOrganization, isPending: isJoining } = useJoinOrganization()
  const listRef = useRef<HTMLDivElement>(null)

  const handleScroll = (e: any) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight
    if (bottom) {
      if (!hasNextMyOrgs || isFetchingMyOrg) return
      fetchNextMyOrgs()
    }
  }

  useEffect(() => {
    if (listRef && listRef.current) {
      const hasScrollbar = listRef.current.scrollHeight > listRef.current.clientHeight
      if (!hasScrollbar) {
        fetchNextMyOrgs()
      }
    }
  }, [fetchNextMyOrgs, myOrgPages])

  return (
    <div className='p-8 space-y-9'>
      <h2 className='text-2xl'>Your organizations</h2>

      <div className='max-w-xl w-full h-96 overflow-auto' ref={listRef} onScroll={handleScroll}>
        <UserOrganizationsList
          organizations={myOrgPages?.pages
            .flat(Infinity)
            .map((r) => r.organizations)
            .flat()
            ?.map((o) => ({ id: o.organizationCode, image: o.icon, name: o.name }))}
          onNotificationsClick={() => setEditModalOpen(true)}
        />
      </div>

      <div className='flex items-center gap-x-3'>
        <Button
          className='bg-white text-blue-500 border border-blue-500'
          onClick={() => setCreateOrganizationOpen(true)}
        >
          Create organization
        </Button>
        <Button className='bg-blue-500 text-white' onClick={() => setJoinOrganizationOpen(true)}>
          Join organization
        </Button>
      </div>

      {joinOrganizationOpen && (
        <JoinOrganizationModal
          open={joinOrganizationOpen}
          setOpen={setJoinOrganizationOpen}
          onJoin={(organization) => {
            joinOrganization(
              { code: organization.code },
              {
                onSuccess: () => {
                  setJoinOrganizationOpen(false)
                  setNotificationMessage('Thank you. Your request has been sent to the admin to approve.')
                  setNotificationModalOpen(true)
                },
              },
            )
          }}
          isJoining={isJoining}
          fetchNext={() => fetchNextOrgs()}
          setSearch={(value: string) => setSearch(value)}
          options={
            orgPages?.pages
              .flat(Infinity)
              .map((r) => r.organizations)
              .flat()
              ?.map((o) => ({
                id: o.organizationCode,
                code: o.organizationCode,
                image: o.icon,
                name: o.name,
              })) ?? []
          }
        />
      )}
      {createOrganizationOpen && (
        <CreateOrganizationModal
          isCreating={isCreating}
          open={createOrganizationOpen}
          setOpen={setCreateOrganizationOpen}
          onContinue={(name: string, subdomain: string) => {
            createOrganization(
              { name, code: subdomain },
              {
                onSuccess: () => {
                  setCreateOrganizationOpen(false)
                  setNotificationMessage(`You've successfully registered ${name} organization.`)
                  setNotificationModalOpen(true)
                },
              },
            )
          }}
        />
      )}
      {notificationModalOpen && (
        <SuccessNotification
          open={notificationModalOpen}
          setOpen={setNotificationModalOpen}
          message={notificationMessage}
        />
      )}
      {editModalOpen && <NotificationModal open={editModalOpen} setOpen={setEditModalOpen} />}
    </div>
  )
}

export default Organizations
